import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Loading } from "@/components/shared";
import { CheckCircle2, AlertTriangle, HelpCircle, ExternalLink, ShieldCheck } from "lucide-react";
import { fmtDate, ageLabel } from "@/lib/register";

const STATE = {
  full: { label: "Fully swept", icon: CheckCircle2, cls: "border-teal-400/40 bg-teal-500/5 text-teal-700", dot: "bg-teal-500" },
  partial: { label: "Partially swept", icon: AlertTriangle, cls: "border-amber-500/40 bg-amber-500/10 text-amber-800", dot: "bg-amber-500" },
  never: { label: "Never swept", icon: HelpCircle, cls: "border-[#DDDAD1] bg-transparent text-slate-500", dot: "bg-slate-400" },
};

export default function Coverage() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/licensee-coverage").then(({ data }) => setData(data)).catch(() => setData(false));
    window.scrollTo(0, 0);
  }, []);

  if (data === null) return <Loading />;
  if (data === false) return <Loading label="Coverage unavailable" />;

  const rows = (data.jurisdictions_in_scope || []).map((j) => {
    const entry = (data.entries || {})[j];
    const state = !entry ? "never" : entry.sweep_type === "partial" ? "partial" : "full";
    return { j, state, entry, employers: data.employer_counts?.[j] ?? 0 };
  });

  return (
    <main className="mx-auto max-w-[1800px] px-6 py-12 sm:px-10 lg:px-16">
      <p className="eyebrow mb-3">Register</p>
      <h1 className="text-4xl font-bold">Coverage</h1>
      <p className="mt-4 max-w-4xl text-slate-400">
        Which official registers we have swept, when, and how completely. Coverage is the difference between "we checked and found nothing" and "we have not checked yet". Absence of a company from a jurisdiction only means something when that register has been fully swept.
      </p>

      <div className="mt-10 overflow-x-auto rounded-[12px] border border-[#DDDAD1]">
        <table className="w-full text-left text-sm" data-testid="coverage-table">
          <thead className="bg-[#EDEBE4] text-xs uppercase tracking-[0.08em] text-slate-500">
            <tr>
              <th className="px-5 py-3 font-mono font-medium">Jurisdiction</th>
              <th className="px-5 py-3 font-mono font-medium">Sweep state</th>
              <th className="px-5 py-3 font-mono font-medium">Last swept</th>
              <th className="px-5 py-3 font-mono font-medium">Licences recorded</th>
              <th className="px-5 py-3 font-mono font-medium">In register</th>
              <th className="px-5 py-3 font-mono font-medium">Source</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(({ j, state, entry, employers }) => {
              const s = STATE[state];
              const Icon = s.icon;
              return (
                <tr key={j} data-testid="coverage-row" className="border-t border-[#DDDAD1]">
                  <td className="px-5 py-4 font-semibold text-ink">
                    <span className="flex items-center gap-2"><span className={`h-2 w-2 rounded-full ${s.dot}`} /> {j}</span>
                  </td>
                  <td className="px-5 py-4">
                    <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold ${s.cls}`}><Icon className="h-3.5 w-3.5" /> {s.label}</span>
                  </td>
                  <td className="px-5 py-4 text-slate-400">
                    {entry ? <span>{fmtDate(entry.last_swept_at)} <span className="text-slate-500">· {ageLabel(entry.last_swept_at)}</span></span> : <span className="text-slate-500">Not yet</span>}
                  </td>
                  <td className="px-5 py-4 text-slate-300">{entry ? entry.licences_recorded : "—"}</td>
                  <td className="px-5 py-4 text-slate-300">{employers} employer{employers === 1 ? "" : "s"}</td>
                  <td className="px-5 py-4">
                    {entry?.register_url ? (
                      <a href={entry.register_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-teal-600 hover:underline">Register <ExternalLink className="h-3 w-3" /></a>
                    ) : <span className="text-slate-500">—</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="mt-6 grid gap-3 sm:grid-cols-3">
        <p className="flex items-start gap-2 text-xs text-slate-500"><CheckCircle2 className="mt-0.5 h-4 w-4 text-teal-500" /> <span><b className="text-slate-300">Fully swept:</b> the whole register was enumerated. A company not listed was not in the register at that time.</span></p>
        <p className="flex items-start gap-2 text-xs text-slate-500"><AlertTriangle className="mt-0.5 h-4 w-4 text-amber-500" /> <span><b className="text-slate-300">Partially swept:</b> only part of the register was captured. Absence does not mean a company is unlicensed.</span></p>
        <p className="flex items-start gap-2 text-xs text-slate-500"><HelpCircle className="mt-0.5 h-4 w-4 text-slate-400" /> <span><b className="text-slate-300">Never swept:</b> we hold no register data for this jurisdiction. This is absence of knowledge, not absence of licensees.</span></p>
      </div>

      <p className="mt-8 flex items-center gap-1.5 text-xs text-slate-500">
        <ShieldCheck className="h-3.5 w-3.5 text-teal-400" /> This is a derived, unofficial register. Each regulator's own published register is the authoritative record.
      </p>
    </main>
  );
}
